import {
  chiffreDe,
  compteDansLeChiffre,
  compterParTranche,
  encaissementsParMoyen,
  jourMural,
  meilleuresVentes,
  periodeEnCours,
  referencesDepuisEan,
  repartitionParCategorie,
  resultatDesVentes,
  serieDesVentes,
  signeDansLeChiffre,
  tranches,
  type MeilleureVente,
  type Part,
  type Pas,
  type PaymentMethod,
  type Periode,
  type PointVentes,
  type ResultatDesVentes,
  type Tranche,
  type Payment,
  type Transaction,
  type TransactionLine,
} from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'


/**
 * Lecture des ventes depuis la réplique locale.
 *
 * ⭐ L'écran de caisse lit ICI, jamais le serveur. C'est ce qui lui permet de
 * rouvrir les tickets du jour sans réseau — et c'est tout l'objet du produit.
 */

function nombre(v: unknown): number {
  return typeof v === 'number' ? v : Number(v ?? 0)
}

function versLigne(row: Record<string, unknown>): TransactionLine {
  return {
    id: String(row['id']),
    position: nombre(row['position']),
    itemId: (row['item_id'] as string) ?? null,
    label: String(row['label'] ?? ''),
    description: (row['description'] as string) ?? null,
    quantity: nombre(row['quantity']),
    unitPrice: nombre(row['unit_price']),
    discountRate: nombre(row['discount_rate']),
    discountAmount: nombre(row['discount_amount']),
    taxRate: nombre(row['tax_rate']),
    subtotal: nombre(row['subtotal']),
    taxAmount: nombre(row['tax_amount']),
    total: nombre(row['total']),
  }
}

function versPaiement(row: Record<string, unknown>): Payment {
  return {
    id: String(row['id']),
    method: row['method'] as Payment['method'],
    amount: nombre(row['amount']),
    // ⚠️ `null` préservé : il dit « non renseigné », ce que `nombre()`
    // écraserait en zéro — c'est-à-dire en « rien n'a été rendu ».
    tendered: row['tendered_amount'] == null ? null : nombre(row['tendered_amount']),
    change: row['change_amount'] == null ? null : nombre(row['change_amount']),
    currencyCode: String(row['currency_code'] ?? ''),
    receivedAt: String(row['received_at'] ?? ''),
    reference: (row['reference'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdByName: (row['created_by_name'] as string) ?? null,
  }
}

export function versTransaction(row: LocalRow): Transaction {
  const brut = row as unknown as Record<string, unknown>
  return {
    id: String(brut['id']),
    docType: (brut['doc_type'] as Transaction['docType']) ?? 'invoice',
    number: (brut['number'] as string) ?? null,
    localRef: (brut['local_ref'] as string) ?? null,
    customerId: (brut['customer_id'] as string) ?? null,
    customerName: (brut['customer_name'] as string) ?? null,
    status: (brut['status'] as Transaction['status']) ?? 'draft',
    issueDate: String(brut['issue_date'] ?? ''),
    dueDate: (brut['due_date'] as string) ?? null,
    currencyCode: String(brut['currency_code'] ?? ''),
    subtotalAmount: nombre(brut['subtotal_amount']),
    discountAmount: nombre(brut['discount_amount']),
    taxAmount: nombre(brut['tax_amount']),
    totalAmount: nombre(brut['total_amount']),
    paidAmount: nombre(brut['paid_amount']),
    taxBreakdown: (brut['tax_breakdown'] as Transaction['taxBreakdown']) ?? [],
    notes: (brut['notes'] as string) ?? null,
    terms: (brut['terms'] as string) ?? null,
    parentId: (brut['parent_id'] as string) ?? null,
    createdOffline: Boolean(brut['created_offline']),
    cashierName: (brut['cashier_name'] as string) ?? null,
    validatedAt: (brut['validated_at'] as string) ?? null,
    createdAt: String(brut['created_at'] ?? ''),
    updatedAt: String(brut['updated_at'] ?? ''),
    version: nombre(brut['version']),
    lines: ((brut['lines'] as Record<string, unknown>[]) ?? []).map(versLigne),
    payments: ((brut['payments'] as Record<string, unknown>[]) ?? []).map(versPaiement),
  }
}

/** Minuscules sans accents, comme ailleurs : « Éric » et « eric » se rencontrent. */
function fold(valeur: unknown): string {
  return String(valeur ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

/**
 * Tris du journal des ventes.
 *
 * ⚠️ La RÉFÉRENCE ne se trie pas sur la chaîne affichée : une vente encaissée
 * hors ligne porte « D-2026-… » ou « L-2026-… » tant que son numéro n'est pas
 * attribué, et le classement alphabétique les rangeait toutes ensemble, loin de
 * leur voisine du même jour. On trie sur ce qui existe, numéro d'abord.
 */
const TRIS_VENTE: Record<string, (a: Transaction, b: Transaction) => number> = {
  date: (a, b) => a.createdAt.localeCompare(b.createdAt),
  '-date': (a, b) => b.createdAt.localeCompare(a.createdAt),
  reference: (a, b) => reference(a).localeCompare(reference(b), 'fr'),
  '-reference': (a, b) => reference(b).localeCompare(reference(a), 'fr'),
  client: (a, b) => (a.customerName ?? '').localeCompare(b.customerName ?? '', 'fr'),
  '-client': (a, b) => (b.customerName ?? '').localeCompare(a.customerName ?? '', 'fr'),
  acteur: (a, b) => (a.cashierName ?? '').localeCompare(b.cashierName ?? '', 'fr'),
  '-acteur': (a, b) => (b.cashierName ?? '').localeCompare(a.cashierName ?? '', 'fr'),
  type: (a, b) => a.docType.localeCompare(b.docType),
  '-type': (a, b) => b.docType.localeCompare(a.docType),
  statut: (a, b) => a.status.localeCompare(b.status),
  '-statut': (a, b) => b.status.localeCompare(a.status),
  total: (a, b) => a.totalAmount - b.totalAmount,
  '-total': (a, b) => b.totalAmount - a.totalAmount,
  reste: (a, b) => resteDuTrie(a) - resteDuTrie(b),
  '-reste': (a, b) => resteDuTrie(b) - resteDuTrie(a),
}

const reference = (t: Transaction): string => t.number ?? t.localRef ?? ''
/** Rien n'est dû sur un devis : il ne doit pas remonter en tête d'un tri. */
const resteDuTrie = (t: Transaction): number =>
  t.docType === 'quote' ? 0 : Math.max(0, t.totalAmount - t.paidAmount)

export interface RequeteVentes {
  limite?: number
  jour?: string
  q?: string
  docType?: Transaction['docType']
  statut?: Transaction['status']
  /**
   * Ne retient que les documents portant AU MOINS une ligne sur cet article.
   *
   * ⭐ C'est ce qui rend utile le clic sur un stock négatif : « quelles
   * commandes expliquent ce trou ? », pas « quelles commandes en général ».
   * Les lignes sont déjà embarquées dans chaque document répliqué — pas besoin
   * d'un second index, un simple filtre suffit.
   */
  itemId?: string
  sort?: string
}

export async function listerVentes(
  storeId: string,
  options: RequeteVentes = {},
): Promise<Transaction[]> {
  const db = dbFor(storeId)
  let lignes = await db.transactions.toArray()

  if (options.jour) {
    lignes = lignes.filter((r) => String((r as Record<string, unknown>)['issue_date']) === options.jour)
  }

  let liste = lignes.map(versTransaction)

  if (options.q?.trim()) {
    /**
     * ⭐ Une DOUCHETTE tape ici comme un clavier.
     *
     * Le code-barres du ticket porte un EAN-13 interne ; scanné, il arrive
     * sous forme de treize chiffres que personne ne reconnaîtrait. On le
     * retraduit en numéro de document AVANT de chercher — sans quoi le geste
     * naturel (« scanne le ticket du client ») ne ramènerait rien.
     */
    const saisie = options.q.trim()
    // Un code scanné donne DEUX numéros possibles — la forme courante et
    // l'ancienne, les préfixes étant passés de l'anglais au français.
    const candidats = referencesDepuisEan(saisie)
    const aiguilles = (candidats.length > 0 ? candidats : [saisie]).map(fold)
    liste = liste.filter((v) =>
      [v.number, v.localRef, v.customerName, v.cashierName].some((champ) =>
        aiguilles.some((aiguille) => fold(champ).includes(aiguille)),
      ),
    )
  }

  if (options.docType) liste = liste.filter((v) => v.docType === options.docType)
  if (options.statut) liste = liste.filter((v) => v.status === options.statut)
  if (options.itemId) {
    const cible = options.itemId
    liste = liste.filter((v) => v.lines.some((l) => l.itemId === cible))
  }

  // Les plus récentes d'abord PAR DÉFAUT : en caisse, on cherche presque
  // toujours le ticket de la minute précédente.
  liste.sort(TRIS_VENTE[options.sort ?? '-date'] ?? TRIS_VENTE['-date']!)

  return liste.slice(0, options.limite ?? 200)
}

export async function lireVente(storeId: string, id: string): Promise<Transaction | undefined> {
  const row = await dbFor(storeId).transactions.get(id)
  return row ? versTransaction(row) : undefined
}

/** Chiffre du jour, calculé localement : le tableau de bord de caisse doit
 *  fonctionner sans réseau comme le reste. */
export async function totauxDuJour(
  storeId: string,
  jour: string,
): Promise<{ ventes: number; encaisse: number }> {
  const ventes = await listerVentes(storeId, { jour, limite: 1000 })
  const retenues = ventes.filter(compteDansLeChiffre)
  return {
    // ⚠️ Un AVOIR n'est pas une vente de plus : il ne se compte pas dans le
    // nombre, et il se SOUSTRAIT du montant.
    ventes: retenues.filter((v) => signeDansLeChiffre(v) === 1).length,
    encaisse: retenues.reduce((somme, v) => somme + signeDansLeChiffre(v) * v.totalAmount, 0),
  }
}

/**
 * Chiffre d'affaires sur une période, plus le panier moyen.
 *
 * ⭐ Calculé depuis la RÉPLIQUE, pas demandé au serveur : le tableau de bord
 * doit rester lisible hors ligne comme le reste de l'application. Les ventes
 * y sont déjà toutes présentes, il n'y a rien à aller chercher.
 *
 * ⚠️ Brouillons, ventes ANNULÉES et DEVIS sont exclus — la règle vit dans
 * `compteDansLeChiffre`, une seule fois pour les quatre écrans qui additionnent
 * de l'argent. Les compter gonflerait le chiffre d'affaires d'un montant que la
 * comptabilité ne reconnaît pas.
 */
/** Prix d'achat et type d'un article, par identifiant. */
export type CoutsArticles = ReadonlyMap<string, { purchasePrice: number; kind: 'product' | 'service' }>

/**
 * Les prix d'achat connus : ceux de la réplique, COMPLÉTÉS par ceux que le
 * serveur a fournis pour les articles supprimés depuis leur vente.
 */
function prixAchatDe(articles: LocalRow[], complements: CoutsArticles): CoutsArticles {
  const carte = new Map(complements)
  for (const a of articles) {
    carte.set(a.id, {
      purchasePrice: Number(a['purchase_price'] ?? 0),
      kind: a['kind'] === 'service' ? 'service' : 'product',
    })
  }
  return carte
}

/**
 * Les articles que citent les ventes mais que la réplique n'a PLUS.
 *
 * ⚠️ Un article supprimé quitte la réplique ; ses ventes passées, elles,
 * restent. Sans son prix d'achat, le bénéfice le comptait pour zéro et
 * criait « prix d'achat manquant » pour un produit qui en avait un. Ces
 * identifiants sont ceux qu'on demande au serveur, qui garde la fiche.
 */
export async function idsArticlesAbsents(storeId: string): Promise<string[]> {
  const db = dbFor(storeId)
  const [lignes, articles] = await Promise.all([db.transactions.toArray(), db.items.toArray()])
  const connus = new Set(articles.map((a) => a.id))
  const absents = new Set<string>()
  for (const v of lignes.map(versTransaction)) {
    if (!compteDansLeChiffre(v)) continue
    for (const l of v.lines) if (l.itemId && !connus.has(l.itemId)) absents.add(l.itemId)
  }
  return [...absents].sort()
}

export async function chiffreAffaires(
  storeId: string,
  depuis: string,
  complements: CoutsArticles = new Map(),
): Promise<{ montant: number; ventes: number; panierMoyen: number } & ResultatDesVentes> {
  const db = dbFor(storeId)
  // ⭐ Lire les articles ICI fait aussi suivre le bénéfice à Dexie : un prix
  // d'achat corrigé au catalogue se répercute sur le tableau de bord aussitôt.
  const [lignes, articles] = await Promise.all([db.transactions.toArray(), db.items.toArray()])
  const retenues = lignes
    .map(versTransaction)
    .filter((v) => compteDansLeChiffre(v) && v.issueDate >= depuis)

  const montant = retenues.reduce((somme, v) => somme + signeDansLeChiffre(v) * v.totalAmount, 0)
  // Le panier moyen se calcule sur les VENTES, avoirs exclus du dénominateur :
  // un avoir n'est pas un passage en caisse.
  const passages = retenues.filter((v) => signeDansLeChiffre(v) === 1).length
  return {
    montant,
    ventes: passages,
    panierMoyen: passages > 0 ? Math.round(montant / passages) : 0,
    ...resultatDesVentes(retenues, prixAchatDe(articles, complements)),
  }
}

/**
 * La série des ventes pour un graphe du tableau de bord.
 *
 * ⭐ Depuis la RÉPLIQUE, comme les cartes : le graphe s'ouvre sans réseau, et
 * sa dernière colonne vaut le chiffre de la carte au franc près.
 *
 * Le pas annuel remonte à la première vente comptée — pas plus loin : des
 * années à zéro avant l'ouverture de la boutique seraient une fausse histoire.
 */
export async function serieVentes(
  storeId: string,
  pas: Pas,
  aujourdhui: string,
  complements: CoutsArticles = new Map(),
): Promise<{ tranches: Tranche[]; points: PointVentes[] }> {
  const db = dbFor(storeId)
  const [lignes, articles] = await Promise.all([db.transactions.toArray(), db.items.toArray()])
  const ventes = lignes.map(versTransaction).filter(compteDansLeChiffre)
  const premiere = ventes.reduce<string | null>(
    (min, v) => (min === null || v.issueDate < min ? v.issueDate : min),
    null,
  )
  const decoupage = tranches(pas, aujourdhui, premiere ? Number(premiere.slice(0, 4)) : undefined)
  return {
    tranches: decoupage,
    points: serieDesVentes(ventes, prixAchatDe(articles, complements), pas, decoupage),
  }
}

/** Tout ce que montre le tableau de bord, calculé d'une seule lecture. */
export interface TableauDeBord {
  /**
   * Le pas des TENDANCES — celui de la période, sauf une exception : en
   * « Année », une boutique qui n'a qu'une année d'activité tracerait des
   * courbes d'un seul point. On passe alors au mois.
   */
  pasTendance: Pas
  tranches: Tranche[]
  /** Une entrée par tranche. */
  points: PointVentes[]
  /** Les chiffres de la période EN COURS, arrêtée à aujourd'hui. */
  actuel: { ca: number; ventes: number; panierMoyen: number | null } & ResultatDesVentes
  courante: Periode
  /** La période précédente arrêtée au même point, ou `null` pour le jour. */
  precedent: Periode | null
  avant: { ca: number; ventes: number; panierMoyen: number | null } | null
  categories: Part[]
  encaissements: { moyen: PaymentMethod; montant: number }[]
  meilleures: MeilleureVente[]
  /** Nouveaux clients, tranche par tranche. */
  nouveauxClients: number[]
  nouveauxClientsActuel: number
  nouveauxClientsAvant: number | null
  clientsTotal: number
}

/**
 * Le tableau de bord, en UNE lecture de la réplique.
 *
 * ⭐ Une seule passe sur les ventes, les articles, les catégories et les
 * clients, plutôt qu'une requête par carte : une dizaine de cartes relisant
 * chacune toutes les ventes feraient ramer un téléphone d'entrée de gamme dès
 * quelques milliers de tickets.
 *
 * @param aujourdhui le jour qu'il est À LA BOUTIQUE — pas sur le poste.
 */
export async function tableauDeBord(
  storeId: string,
  pas: Pas,
  aujourdhui: string,
  fuseau: string,
  complements: CoutsArticles = new Map(),
): Promise<TableauDeBord> {
  const db = dbFor(storeId)
  const [lignes, articles, categories, clients] = await Promise.all([
    db.transactions.toArray(),
    db.items.toArray(),
    db.categories.toArray(),
    db.customers.toArray(),
  ])
  const ventes = lignes.map(versTransaction).filter(compteDansLeChiffre)
  const premiere = ventes.reduce<string | null>(
    (min, v) => (min === null || v.issueDate < min ? v.issueDate : min),
    null,
  )
  const premiereAnnee = premiere ? Number(premiere.slice(0, 4)) : undefined
  const pasTendance: Pas =
    pas === 'annee' && (premiereAnnee ?? Number(aujourdhui.slice(0, 4))) >= Number(aujourdhui.slice(0, 4))
      ? 'mois'
      : pas
  const decoupage = tranches(pasTendance, aujourdhui, premiereAnnee)
  const { courante, precedent } = periodeEnCours(pas, aujourdhui)
  const prixAchat = prixAchatDe(articles, complements)
  const dans = (d: string, p: Periode): boolean => d >= p.du && d <= p.au

  // Le jour de création de chaque client, à l'horloge de la BOUTIQUE.
  const creations = clients
    .map((c) => String(c['created_at'] ?? ''))
    .filter(Boolean)
    .map((iso) => jourMural(iso, fuseau))

  return {
    pasTendance,
    tranches: decoupage,
    points: serieDesVentes(ventes, prixAchat, pasTendance, decoupage),
    // ⚠️ Calculés sur la PÉRIODE, pas lus dans la dernière tranche : en
    // « Année » tracée au mois, la dernière tranche serait le mois en cours.
    actuel: {
      ...chiffreDe(ventes, courante),
      ...resultatDesVentes(
        ventes.filter((v) => dans(v.issueDate, courante)),
        prixAchat,
      ),
    },
    courante,
    precedent,
    avant: precedent ? chiffreDe(ventes, precedent) : null,
    categories: repartitionParCategorie(
      ventes,
      new Map(articles.map((a) => [a.id, (a['category_id'] as string | null) ?? null])),
      new Map(categories.map((c) => [c.id, String(c['name'] ?? '')])),
      courante,
    ),
    encaissements: encaissementsParMoyen(ventes, courante, fuseau),
    meilleures: meilleuresVentes(ventes, courante, pasTendance, decoupage),
    nouveauxClients: compterParTranche(creations, pasTendance, decoupage),
    nouveauxClientsActuel: creations.filter((d) => dans(d, courante)).length,
    nouveauxClientsAvant: precedent ? creations.filter((d) => dans(d, precedent)).length : null,
    clientsTotal: clients.length,
  }
}

/**
 * Une FACTURE est-elle née de cette commande, dans la réplique ?
 *
 * ⭐ C'est ce que le navigateur sait AVANT le serveur. Une commande
 * transformée en caisse laisse sa facture dans la file de synchronisation :
 * elle existe ici, pas encore là-bas. Demander au serveur d'annuler la
 * commande à ce moment-là, c'était lui faire accepter ce qu'il aurait refusé
 * une minute plus tard — et rendre deux fois la réservation.
 *
 * Même règle que le serveur : toute facture qui cite la commande, annulée ou
 * non.
 */
export async function factureIssueDe(storeId: string, commandeId: string): Promise<boolean> {
  const enfant = await dbFor(storeId)
    .transactions.filter(
      (r) => r['parent_id'] === commandeId && r['doc_type'] === 'invoice',
    )
    .first()
  return enfant !== undefined
}

/**
 * Référence de chaque document, indexée par identifiant.
 *
 * ⭐ Sert à nommer le PARENT d'un document : une facture née d'un devis porte
 * l'identifiant de ce devis, pas son numéro. Sans cette table, le journal ne
 * pourrait afficher qu'un UUID — c'est-à-dire rien.
 */
export async function referencesParId(
  storeId: string,
): Promise<Map<string, { reference: string; docType: string }>> {
  const lignes = await dbFor(storeId).transactions.toArray()
  return new Map(
    lignes.map((r) => {
      const brut = r as unknown as Record<string, unknown>
      return [
        String(brut['id']),
        {
          reference: (brut['number'] as string) ?? (brut['local_ref'] as string) ?? '—',
          docType: String(brut['doc_type'] ?? 'invoice'),
        },
      ]
    }),
  )
}

/**
 * Documents rattachés à un client, pour l'onglet Transactions de sa fiche.
 *
 * ⚠️ Les DEVIS y figurent, bien qu'ils soient des brouillons. C'est même là
 * qu'on les cherche : « je lui ai fait un devis la semaine dernière ». Les
 * autres brouillons — une facture commencée puis abandonnée — restent masqués,
 * ils n'ont rien à dire au client.
 */
export async function ventesDuClient(storeId: string, customerId: string): Promise<Transaction[]> {
  const db = dbFor(storeId)
  const lignes = await db.transactions.toArray()
  return lignes
    .map(versTransaction)
    .filter(
      (v) =>
        v.customerId === customerId && (v.status !== 'draft' || v.docType === 'quote'),
    )
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
}
